/**
 * Smooth Scroll for Anchor Links
 *
 * Provides smooth scrolling behavior for anchor links on the page.
 * Handles both same-page anchors and cross-page anchors with hash fragments.
 *
 * @package Karuna_Hospice
 */

export function initSmoothScroll() {
	/**
	 * Handles smooth scrolling to anchor targets
	 */
	function handleSmoothScroll(event) {
		const link = event.target.closest('a');
		
		if (!link) {
			return;
		}

		const href = link.getAttribute('href');

		// Only handle links that contain # (anchor links)
		if (!href || !href.includes('#')) {
			return;
		}

		// Extract the hash from the href
		const hashIndex = href.indexOf('#');
		const hash = hashIndex !== -1 ? href.substring(hashIndex + 1) : '';
		
		if (!hash) {
			return;
		}

		// Find the target element
		const targetElement = document.getElementById(hash);

		// If target element doesn't exist, let the browser handle it normally
		if (!targetElement) {
			return;
		}

		// Check if the link is to the same page or external
		let isSamePage = false;
		try {
			const linkUrl = new URL(link.href, window.location.href);
			const currentUrl = new URL(window.location.href);
			isSamePage = linkUrl.pathname === currentUrl.pathname;
		} catch (e) {
			// If URL parsing fails, check if it's a relative anchor link
			isSamePage = link.getAttribute('href').startsWith('#');
		}
		
		// Also check for relative anchor links
		if (!isSamePage) {
			isSamePage = link.getAttribute('href').startsWith('#');
		}

		// If it's a same-page anchor, prevent default and smooth scroll
		if (isSamePage) {
			event.preventDefault();

			// Calculate offset (accounting for fixed header if needed)
			const header = document.getElementById('masthead');
			const headerHeight = header ? header.offsetHeight : 0;
			const offset = 20; // Additional offset for spacing
			const targetPosition = targetElement.offsetTop - headerHeight - offset;

			// Smooth scroll to target
			window.scrollTo({
				top: Math.max(0, targetPosition), // Ensure we don't scroll to negative position
				behavior: 'smooth'
			});

			// Update URL hash without triggering scroll
			history.pushState(null, null, `#${hash}`);
		}
		// If it's a cross-page link with hash, let it navigate normally
		// The browser will handle the navigation, and we can add smooth scroll on page load
	}

	/**
	 * Handles smooth scroll on page load if URL contains a hash
	 */
	function handleHashOnLoad() {
		if (window.location.hash) {
			const hash = window.location.hash.substring(1);
			const targetElement = document.getElementById(hash);

			if (targetElement) {
				// Small delay to ensure page is fully loaded
				setTimeout(() => {
					const header = document.getElementById('masthead');
					const headerHeight = header ? header.offsetHeight : 0;
					const offset = 20;
					const targetPosition = targetElement.offsetTop - headerHeight - offset;

					window.scrollTo({
						top: targetPosition,
						behavior: 'smooth'
					});
				}, 100);
			}
		}
	}

	// Initialize on DOM ready
	function init() {
		// Use event delegation to handle all anchor links (including dynamically added ones)
		document.addEventListener('click', handleSmoothScroll);

		// Handle hash on page load
		handleHashOnLoad();
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		// DOM is already ready
		init();
	}
}

